import type { components } from '../../generated/fct';
import { ApiError, ApiRequest } from '../shared';
import { ConditionTemplate } from './condition-template';
import { FctReport } from './report.model';
import {
  ReportFinding,
  ReportFindingConditionDetail,
  ReportFindingStatus,
} from './report-finding.model';

export type ActivityLogNormaliseCheckItem = {
  defaultBool: boolean;
  key: number;
  userModifiedBool: boolean | null;
  value: string;
};

export type ActivityLogNormaliseCheck = Record<string, ActivityLogNormaliseCheckItem>;

export type ActivityLogCustomNormals = Record<string, string>;

export type ActivityLogDetailValue = boolean | null | number | string | string[];

type ObjectDiffDetails = {
  [key: string]: {
    new:
      | ActivityLogCustomNormals
      | ActivityLogNormaliseCheck
      | boolean
      | null
      | number
      | ReportFindingConditionDetail[]
      | string;
    old:
      | ActivityLogCustomNormals
      | ActivityLogNormaliseCheck
      | boolean
      | null
      | number
      | ReportFindingConditionDetail[]
      | string;
  };
};

type UpdatedReportObject = FctReport & {
  createdAt?: string;
  createdBy?: string;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  patientGender?: string;
  publishedAt?: null | string;
  publishedBy?: null | string;
};

export type FullObject = {
  actionDetails?: string;
  actionSummary?: string;
  additionalRecommendations?: null | string;
  aiAcknowledged?: boolean;
  aiFinding?: boolean;
  authors?: string[];
  changeFromPrevious?: boolean | null;
  clinicalNotes?: null | string;
  conditionDetail?: ReportFindingConditionDetail[];
  createdAt?: string;
  createdBy?: string;
  csdScore?: number;
  customNormals?: ActivityLogCustomNormals | null;
  dateOfPrevious?: null | string;
  descriptionOfCall?: string;
  enabled?: boolean;
  excludeDicomImages?: string[];
  finalImpressions?: string;
  finding?: string;
  findingDetails?: string;
  findingSummary?: string;
  incompleteScan?: boolean;
  isAutoSelectEnabled?: boolean;
  keyImages?: string[];
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  newFinding?: boolean;
  nextRecommendedScan?: number;
  normaliseCheck?: ActivityLogNormaliseCheck;
  observation?: string;
  oncoradsScore?: number;
  organ?: string;
  patientGender?: string;
  publishedAt?: null | string;
  publishedBy?: null | string;
  reasonForAccessingPtInfo?: string;
  report?: null | UpdatedReportObject;
  reportFindings?: ReportFinding[];
  reportFindingsUuids?: string[];
  reportUuid?: string;
  sensitive?: boolean;
  status?: ReportFindingStatus;
  studyId?: string;
  studySku?: string;
  technique?: string;
  techniqueNotes?: string;
  template?: ConditionTemplate | null;
  templateUuid?: string;
  urgent?: boolean;
  userSession?: boolean;
  uuid?: string;
};

export type ActivityLogAction = 'REJECT' | components['schemas']['AuditAction'];

export const ACTIVITY_LOG_ACTION = {
  CREATE: 'CREATE',
  DELETE: 'DELETE',
  READ: 'READ',
  REJECT: 'REJECT',
  UPDATE: 'UPDATE',
} as const satisfies Record<string, ActivityLogAction>;

type GeneratedActivityLog = components['schemas']['ActivityLogSchema-Output'];

export type ActivityLog = Omit<
  GeneratedActivityLog,
  'action' | 'fullObject' | 'objectDiff' | 'timestamp' | 'uuid'
> &
  Required<Pick<GeneratedActivityLog, 'uuid'>> & {
    action: ActivityLogAction;
    fullObject: FullObject | null;
    objectDiff: null | ObjectDiffDetails;
    timestamp: string;
  };

export type ActivityLogsParams = {
  action: ActivityLogAction | null;
  actor: null | string;
  page: number;
  related_object_uuid: null | string;
  report_uuid: null | string;
  size: number;
  study_id: string;
  timestamp_end: null | string;
  timestamp_start: null | string;
};

export interface ActivityLogsPayload {
  params: Partial<ActivityLogsParams>;
}

export type ActivityLogsRequest = ApiRequest<ActivityLogsPayload>;

export type ActivityLogsResponse = Omit<
  components['schemas']['PaginatedList_ActivityLogSchema_'],
  'items'
> & {
  items: ActivityLog[];
};

export type ActivityLogsError = ApiError<unknown>;

export type PostActivityLogPayload = Omit<
  components['schemas']['ActivityLogSchema-Input'],
  'action' | 'fullObject' | 'objectDiff'
> & {
  action: ActivityLogAction;
  fullObject: null | Record<string, unknown>;
  objectDiff: null | Record<string, unknown>;
};

export type PostActivityLogRequest = ApiRequest<PostActivityLogPayload>;

export type PostActivityLogResponse = Omit<
  components['schemas']['ActivityLogSchema-Output'],
  'action' | 'fullObject' | 'objectDiff' | 'studyId' | 'timestamp' | 'uuid'
> & {
  action: ActivityLogAction;
  fullObject: null | Record<string, unknown>;
  objectDiff: null | Record<string, unknown>;
  studyId: string;
  timestamp: string;
  uuid: string;
};

export type PostActivityLogError = ApiError<unknown>;
